import { get, map, find } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import Button from 'components/Button';
import { SecondaryHeading } from 'components/Headings';
import Image from 'components/ImageV2';
import Modal from 'components/Modal';
import Paragraph from 'components/Paragraph';
import Select from 'components/Select';
import { Sub } from 'components/Sub';
import { IMAGE_RATIOS, IMAGE_WIDTHS } from 'constants/images';
import { small, medium, largeUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';
import { navigateToUrl } from 'utils/Url';

const optionPropType = PropTypes.shape({
  shopID: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  showModal: PropTypes.bool.isRequired,
  redirectUrl: PropTypes.string,
  modalHeading: PropTypes.string,
  modalImage: PropTypes.shape({}),
  modalCopy: PropTypes.string,
});

const ReturnsPageCountrySelector = class extends React.PureComponent {
  static propTypes = {
    options: PropTypes.arrayOf(optionPropType).isRequired,
    defaultOption: optionPropType.isRequired,
    labelText: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    isModalOpen: PropTypes.bool.isRequired,
    openModal: PropTypes.func.isRequired,
    closeModal: PropTypes.func.isRequired,
    modalSettings: PropTypes.shape({}),
    onSubmit: PropTypes.func.isRequired,
  };

  static defaultProps = {
    modalSettings: {},
  };

  constructor(props) {
    super(props);
    const { defaultOption } = props;

    this.state = {
      selectedOption: defaultOption,
    };
  }

  handleSubmit = () => {
    const { openModal, onSubmit } = this.props;
    const { selectedOption } = this.state;

    const shopID = get(selectedOption, 'shopID');
    onSubmit(shopID);

    if (selectedOption.showModal) {
      openModal();
    } else if (selectedOption.redirectUrl) {
      navigateToUrl(selectedOption.redirectUrl);
    }
  };

  handleChange = e => {
    const { options } = this.props;
    const nextOption = find(options, ({ shopID }) => shopID === e.target.value);

    this.setState({ selectedOption: nextOption });
  };

  render() {
    const { options, labelText, buttonText, isModalOpen, closeModal, modalSettings } = this.props;
    const { selectedOption } = this.state;

    return (
      <div className="ReturnsPageCountrySelector">
        <Modal
          isOpen={isModalOpen}
          className="ReturnsPageCountrySelector__modal"
          portalClassName="ReturnsPageCountrySelector__portal"
          onRequestClose={closeModal}
          {...modalSettings}
        >
          <div className="ReturnsPageCountrySelector__modal-content">
            {get(selectedOption, 'showModal') && (
              <>
                <SecondaryHeading className="ReturnsPageCountrySelector__modal-heading">
                  {get(selectedOption, 'modalHeading')}
                </SecondaryHeading>

                <Image
                  className="ReturnsPageCountrySelector__modal-image"
                  src={get(selectedOption, ['modalImage', 'file', 'url'])}
                  alt={get(selectedOption, ['modalImage', 'description'])}
                  width={IMAGE_WIDTHS.W_600}
                  ratio={IMAGE_RATIOS.R_1x1}
                />

                <Paragraph className="ReturnsPageCountrySelector__modal-copy">
                  {get(selectedOption, 'modalCopy')}
                </Paragraph>
              </>
            )}
          </div>
        </Modal>

        <Sub className="ReturnsPageCountrySelector__dropdown-heading">{labelText}</Sub>

        <div className="ReturnsPageCountrySelector__dropdown-wrapper">
          <div className="ReturnsPageCountrySelector__select-wrapper">
            <Select
              id="ReturnsPageCountrySelector__select"
              value={get(selectedOption, 'shopID')}
              onChange={this.handleChange}
              ariaLabel={labelText}
            >
              {map(options, ({ shopID, displayName }) => (
                <option key={shopID} value={shopID}>
                  {displayName}
                </option>
              ))}
            </Select>
          </div>

          <Button
            className="ReturnsPageCountrySelector__dropdown-button"
            onClick={this.handleSubmit}
          >
            {buttonText}
          </Button>
        </div>

        <style jsx>{`
          .ReturnsPageCountrySelector {
          }

          .ReturnsPageCountrySelector__dropdown-wrapper {
            display: flex;
          }

          .ReturnsPageCountrySelector__select-wrapper {
            flex-grow: 1;
          }

          .ReturnsPageCountrySelector__modal-content {
            text-align: center;
          }

          @media ${small} {
            .ReturnsPageCountrySelector__dropdown-wrapper {
              flex-direction: column;
            }

            .ReturnsPageCountrySelector__select-wrapper {
              margin-bottom: ${Theme.get('spacing') * 5}px;
            }

            .ReturnsPageCountrySelector__modal-content {
              padding: 0 ${Theme.get('spacing') * 3}px;
            }
          }

          @media ${medium} {
            .ReturnsPageCountrySelector__modal-content {
              padding: 0 ${Theme.get('spacing') * 9}px;
            }
          }

          @media ${largeUp} {
            .ReturnsPageCountrySelector__modal-content {
              max-width: 400px;
            }
          }
        `}</style>
        <style jsx global>{`
          .ReturnsPageCountrySelector__dropdown {
            width: 100%;
          }

          p.Sub.ReturnsPageCountrySelector__dropdown-heading {
            margin-bottom: ${Theme.get('spacing') / 2}px;
            color: ${Theme.get('colors-black-80')};
          }

          .ReturnsPageCountrySelector__dropdown-button {
            margin-left: ${Theme.get('spacing') * 3}px;
          }

          h2.-secondary-heading.ReturnsPageCountrySelector__modal-heading {
            order: 1;
          }

          .ReturnsPageCountrySelector__modal-image {
            width: 290px;
            order: 2;
          }

          p.Paragraph.ReturnsPageCountrySelector__modal-copy {
            margin-bottom: 0;
            order: 3;
          }

          @media ${small} {
            .ReturnsPageCountrySelector__dropdown-button {
              margin-left: 0;
            }

            .ReturnsPageCountrySelector__modal-image {
              width: 170px;
            }
          }

          @media ${largeUp} {
            h2.-secondary-heading.ReturnsPageCountrySelector__modal-heading {
              margin-bottom: ${Theme.get('spacing') * 4}px;
              order: 2;
            }

            .ReturnsPageCountrySelector__modal-image {
              order: 1;
            }
          }
        `}</style>
      </div>
    );
  }
};

export default ReturnsPageCountrySelector;
